import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { useState } from "react";
import { Alert, Snackbar, Stack } from "@mui/material";
import { useMutation } from "@apollo/client";
import PageTitle from "../widgets/PageTitle";
import PricingMark from "../widgets/PricingMark";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { gridActionSx } from "../widgets/gridActionSx";
import {
  ADD_DASHBOARD_QUERY,
  GET_DIAMS,
  tokenOptions,
} from "../../backend/graphql";
import {
  GraphQLDataGrid,
  GraphQLGridField,
  GraphQLGridParameters,
  parseSavedColumns,
} from "../widgets/GraphQLDataGrid";
import SavedQueryDialog, { SavedQueryDraft } from "./SavedQueryDialog";
import { useAppSelector } from "../../app/hooks";
import {
  Roles,
  seesAdminData,
  seesGrading,
} from "../../app/features/auth/authTypes";

export const handleNavigate = (row: any, navigate: any) => {
  if (row.mainImageID) {
    window.open("/analytics/fromimage/" + row.mainImageID, "_blank");
  } else {
    window.open("/analytics/fromdiamond/" + row.id, "_blank");
  }
};

export const handleNavigatePrice = (row: any, navigate: any) => {
  window.open("/analytics/diamondpricing/" + row.id, "_blank");
};

// A stone can be graded when it has a picture its color is read from: a Langerman original or
// pattern.
export const canGrade = (row: any) =>
  row.source === "Langerman" && (row.hasOriginalImage || row.hasPatternImage);

export const handleNavigateGrading = (row: any) => {
  window.open("/analytics/grading/" + row.id, "_blank");
};

// The grading analysis action of a stone row, for the stone grids, offered to whoever sees the page
// (seesGrading).
export const gradingActions = (row: any, shown: boolean) =>
  shown && canGrade(row)
    ? [
        <GridActionsCellItem
          key="grading"
          icon={<LeaderboardIcon />}
          label="Grading"
          sx={gridActionSx}
          onClick={() => handleNavigateGrading(row)}
        />,
      ]
    : [];

// The Pricing column of the stone grids.
export const renderPricing = (value: number) => (
  <PricingMark percentile={value} />
);

// The stone's admin tags, offered as a column of every stone grid: to whoever sees admin data, and
// hidden until they turn it on.
export const adminTagsColumn = (role: Roles): GraphQLGridField[] =>
  seesAdminData(role)
    ? [{ field: "adminTags", label: "Admin Tags", flex: 1, visible: false }]
    : [];

const gridParameters: (
  source: string | undefined,
  role: Roles,
  isDeveloper: boolean,
  onSaveQuery: (filter: string, columns: string) => void,
) => GraphQLGridParameters = (source, role, isDeveloper, onSaveQuery) => {
  return {
    graphqlQuery: GET_DIAMS,
    graphqlFilter: source ? 'source == "' + source + '"' : undefined,
    // The stock is looked up by SKU more than by anything else, so the toolbar offers it directly.
    quickSearchField: "sKU",
    graphqlSaveQuery:
      source === "Langerman" && role === "Admin"
        ? { label: "Save Query to Alerts", onSave: onSaveQuery }
        : undefined,
    graphqlItemsPath: (data) => data.diamonds,
    rowHeight: 64,
    fields: [
      { field: "representationImageID", label: "Image", type: "image" },
      { field: "id", label: "ID", visible: false, width: 300, type: "uuid" },
      {
        field: "source",
        label: "Source",
        flex: 0.5,
        visible: source ? false : true,
      },
      { field: "sKU", label: "SKU", width: 80, defaultSort: "asc" },
      { field: "carat", label: "Carat", width: 80, type: "number" },
      { field: "cut", label: "Cut", visible: false },
      { field: "shape", label: "Shape", visible: false },
      { field: "clarity", label: "Clarity", width: 80 },
      { field: "color", label: "Color", flex: 2 },
      {
        field: "langermanColor",
        label: "Langerman Color",
        flex: 2,
        visible: source === "Langerman",
      },
      { field: "flat", label: "Flat", type: "number", visible: false },
      { field: "melee", label: "Melee", type: "number", visible: false },
      {
        field: "isGIACertified",
        label: "GIA?",
        flex: 0.5,
        type: "boolean",
        visible: false,
      },
      { field: "tags", label: "Tags", flex: 1 },
      ...adminTagsColumn(role),
      // Notes the back-end tasks leave for an admin: only offered while browsing as one.
      { field: "price", label: "Price", flex: 0.5, type: "number" },
      {
        field: "currentPricePercentile",
        label: "Pricing",
        flex: 0.5,
        type: "number",
        renderCell: (params: any) => renderPricing(params.value),
      },
      { field: "pricingRemark", label: "Remark", flex: 2, visible: false },
      {
        field: "lastFantasyUpdate",
        label: "Last Check",
        type: "dateTimeShort",
        visible: false,
      },
      {
        field: "ignorePricing",
        label: "Ignore Price",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "noLongerAvailable",
        label: "Unavailable",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "hasGoodImage",
        label: "Good image",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "hasWebImage",
        label: "Web image",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "hasOriginalImage",
        label: "Camera image",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "hasPatternImage",
        label: "Pattern image",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "hasCalibratedImage",
        label: "Calibrated image",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "hasImageProcessingError",
        label: "Image Processing Error",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "hasApproximateCalibration",
        label: "Approximate Calibration",
        type: "boolean",
        width: 100,
        visible: false,
      },
    ],
    // Analyse, pricing and — for whoever sees it — grading.
    maxActions: seesGrading(role, isDeveloper) ? 3 : 2,
    getActions: ({ id, navigate, row }) => {
      // We create a list of GridActionsCellItem where we add items depending on conditions

      let actions = [];

      // Image analysis is Langerman-only; other sources have no pipeline to analyse.
      if (row.source === "Langerman") {
        actions.push(
          <GridActionsCellItem
            icon={<TroubleshootIcon />}
            label="Analyse"
            sx={gridActionSx}
            onClick={() => handleNavigate(row, navigate)}
          />,
        );
      }
      if (
        row.price &&
        row.clarity &&
        row.clarity !== "Unknown" &&
        row.color &&
        row.color !== "Unknown" &&
        (!row.tags || !row.tags.includes("BOX"))
      ) {
        actions.push(
          <GridActionsCellItem
            icon={<AttachMoneyIcon />}
            label="Pricing"
            sx={gridActionSx}
            onClick={() => handleNavigatePrice(row, navigate)}
          />,
        );
      }
      actions.push(...gradingActions(row, seesGrading(role, isDeveloper)));

      return actions;
    },
  };
};

type StockProps = {
  source?: string;
};

const Stock = ({ source }: StockProps) => {
  const role = useAppSelector((state) => state.auth.role);
  const isDeveloper = useAppSelector((state) => state.auth.isDeveloper);
  const token = useAppSelector((state) => state.auth.token);
  // The view being saved to the alerts: the grid's filter and visible columns, completed in the
  // same form a saved query is edited in.
  const [saving, setSaving] = useState<SavedQueryDraft | null>(null);
  const [saved, setSaved] = useState<boolean | null>(null);
  const [addQuery, { loading }] = useMutation(ADD_DASHBOARD_QUERY, {
    ...tokenOptions(token),
    refetchQueries: ["SavedQueries"],
  });
  const parameters = gridParameters(
    source,
    role,
    isDeveloper,
    (filter, columns) =>
      setSaving({
        name: "",
        section: "",
        query: filter,
        adminOnly: false,
        columns: parseSavedColumns(columns, parameters.fields) ?? [],
      }),
  );

  const handleSave = async (draft: SavedQueryDraft) => {
    const result = await addQuery({
      variables: { ...draft, columns: JSON.stringify(draft.columns) },
    });
    setSaved(!!result.data?.addDashboardQuery?.success);
    setSaving(null);
  };

  return (
    <Stack spacing={1} useFlexGap>
      <PageTitle />
      <GraphQLDataGrid parameters={parameters} elevated />
      {saving && (
        <SavedQueryDialog
          title="Save query to alerts"
          initial={saving}
          fields={parameters.fields}
          saving={loading}
          onClose={() => setSaving(null)}
          onSave={handleSave}
        />
      )}
      <Snackbar
        open={saved !== null}
        autoHideDuration={6000}
        onClose={() => setSaved(null)}
      >
        <Alert
          severity={saved ? "success" : "error"}
          onClose={() => setSaved(null)}
        >
          {saved ? "Query saved to alerts." : "Unable to save the query."}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Stock;

export const Fantasy = () => {
  return <Stock source="Langerman" />;
};

export const FullStock = () => {
  return <Stock />;
};
